import { FASTAPI_BASE_URL } from "../../config";

const ASK_AI_URL = `${FASTAPI_BASE_URL}/ask_ai`;
const AI_EXPLAINED_CONTENT_DB = "saras";

export const AI_EXPLAINED_MAX_QUESTION_CHARS = 320;

// Representative ages intentionally land in three distinct ask_ai style bands.
// We pass these on each exhibit question instead of mutating the shared kiosk
// Student record. The selected tier remains the source of truth for the visit.
const AI_EXPLAINED_AUDIENCE_PROFILES = {
  el: { age: 9, ageBand: "under13", label: "Elementary school" },
  ms: { age: 13, ageBand: "13-16", label: "Middle school" },
  hs: { age: 17, ageBand: "16-18", label: "High school & adults" },
};

export function getAIExplainedAudienceProfile(tier = "hs") {
  return AI_EXPLAINED_AUDIENCE_PROFILES[tier] || AI_EXPLAINED_AUDIENCE_PROFILES.hs;
}

async function parseErrorResponse(response) {
  try {
    const data = await response.json();
    return data?.detail || data?.message || JSON.stringify(data);
  } catch (_e) {
    try {
      return await response.text();
    } catch (_ignored) {
      return `HTTP ${response.status}`;
    }
  }
}

async function postAsk(body, { signal, onEvent }) {
  const response = await fetch(ASK_AI_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Content-DB": AI_EXPLAINED_CONTENT_DB,
    },
    body: JSON.stringify({
      ...body,
      stream_response: true,
    }),
    signal,
  });

  if (!response.ok) {
    const message = await parseErrorResponse(response);
    throw new Error(message || `Ask AI failed with HTTP ${response.status}`);
  }

  const contentType = String(response.headers.get("content-type") || "").toLowerCase();

  // Moderation and other early ask_ai exits can return normal JSON even though
  // the caller requested streaming. That is still the final response from the
  // SAME /api/ask_ai request, so render it normally.
  if (contentType.includes("application/json")) {
    const data = await response.json();
    const text = data?.["AI response"] || data?.["Formatted AI response"] || "";
    if (text) {
      onEvent?.({
        type: "text_done",
        text,
        formatted: data?.["Formatted AI response"] || text,
      });
    }
    onEvent?.({ type: "done", ...data });
    return data;
  }

  if (!response.body || !response.body.getReader) {
    throw new Error("Streaming response body is not available in this browser.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let pending = "";
  let liveText = "";
  let finalText = "";
  let finalFormatted = "";
  let finalMeta = {};

  const consumeLine = (line) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) return;

    let event;
    try {
      event = JSON.parse(trimmed);
    } catch (_e) {
      return;
    }

    onEvent?.(event);

    if (event.type === "text_delta") {
      liveText += event.delta || "";
    } else if (event.type === "text_done") {
      finalText = event.text || liveText;
      finalFormatted = event.formatted || finalText;
    } else if (event.type === "done") {
      finalMeta = { ...finalMeta, ...event };
    } else if (event.type === "error") {
      throw new Error(event.message || "Ask AI stream error");
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    pending += decoder.decode(value, { stream: true });
    const lines = pending.split("\n");
    pending = lines.pop() || "";

    for (const line of lines) {
      consumeLine(line);
    }
  }

  pending += decoder.decode();
  if (pending.trim()) consumeLine(pending);

  finalText = finalText || liveText;
  finalFormatted = finalFormatted || finalText;

  if (!String(finalText || "").trim()) {
    throw new Error("Streaming completed without usable text.");
  }

  return {
    "AI response": finalText,
    "Formatted AI response": finalFormatted,
    Description: finalMeta.Description || [],
    Video: finalMeta.Video || [],
    PWO: finalMeta.PWO || [],
    Audio: finalMeta.Audio || null,
    allow_reference_fetch: finalMeta.allow_reference_fetch !== false,
    reference_block_reason: finalMeta.reference_block_reason || null,
  };
}

export async function askAIExplained({
  userId,
  clientSessionId,
  question,
  language = "en",
  newDialog = false,
  curatedQuestionId = null,
  questionSource = "curated",
  exhibitVersion = "2.0",
  audienceTier = "hs",
  speakerMuted = true,
  signal,
  onEvent,
}) {
  const cleanQuestion = String(question || "").trim();

  if (!cleanQuestion) {
    throw new Error("Please enter a question about AI.");
  }

  if (cleanQuestion.length > AI_EXPLAINED_MAX_QUESTION_CHARS) {
    throw new Error(
      `Please keep the question under ${AI_EXPLAINED_MAX_QUESTION_CHARS} characters.`
    );
  }

  const audienceProfile = getAIExplainedAudienceProfile(audienceTier);

  const body = {
    user_id: userId,
    client_session_id: clientSessionId,
    question: cleanQuestion,
    new_dialog: !!newDialog,
    context: {
      type: "question",
    },
    ai_explained_context: {
      exhibit_id: "ai_explained",
      exhibit_version: exhibitVersion,
      screen_id: "ask_ai",
      question_source: questionSource,
      curated_question_id: curatedQuestionId,
      visitor_language: language,
      audience_tier: audienceTier,
      audience_label: audienceProfile.label,
      representative_age: audienceProfile.age,
    },
    preferred_language: language,
    age: audienceProfile.age,
    age_band: audienceProfile.ageBand,
    include_references: false,
    restrict_to_rag_only: false,
    answer_length: "short",
    ai_mode: "cloud_realtime",

    // Reuse the existing /api/ask_ai streaming TTS contract. When unmuted,
    // the backend can emit sequential audio_chunk events alongside text.
    is_speaker_muted: !!speakerMuted,

    content_db: AI_EXPLAINED_CONTENT_DB,
  };

  // Phase 6 used an automatic second non-stream request if a stream failed.
  // For open-ended questions that could duplicate the visitor's turn inside
  // the same thread. Phase 7 deliberately uses one request per question and
  // lets the UI offer a clean retry instead.
  return postAsk(body, {
    signal,
    onEvent,
  });
}


export async function logAIExplainedQuestionEngagement({ userId }) {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) return { ok: false, skipped: true };

  const response = await fetch(
    `${FASTAPI_BASE_URL}/student/${encodeURIComponent(cleanUserId)}/update-engagement`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clicks: 0,
        quiz_attempts: 0,
        time_active_seconds: 0,
        pwo_launches: 0,
        video_plays: 0,
        ai_questions: 1,
      }),
    }
  );

  if (!response.ok) {
    const message = await parseErrorResponse(response);
    throw new Error(message || `Engagement logging failed with HTTP ${response.status}`);
  }

  return response.json();
}
